import { Component, OnInit, Inject, ViewChild, ChangeDetectorRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { PaginatorModule } from 'primeng/paginator';
import { TagModule } from 'primeng/tag';
import { AvatarModule } from 'primeng/avatar';
import { TooltipModule } from 'primeng/tooltip';
import { MenuModule } from 'primeng/menu';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { DrawerModule } from 'primeng/drawer';
import { DialogModule } from 'primeng/dialog';
import { TextareaModule } from 'primeng/textarea';
import { MessageService, ConfirmationService } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { ContractService } from '../../core/services/contract.service';
import { Contract, PagedContractsRequest, PagedContractsResponse } from '../../core/models/contract.model';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { HasPermissionDirective } from '../../core/directives/has-permission.directive';
import { AuthService } from '../../core/services/auth';
import { PermissionService } from '../../core/services/permission.service';
import { SubscriptionContextService } from '../../core/services/subscription-context.service';
import { SubscriberService } from '../../core/services/subscriber.service';
import { LoadingSpinnerComponent } from '../../shared/components/loading-spinner/loading-spinner.component';
import { GlobalLoadingService } from '../../core/services/global-loading.service';
import { GlobalLoadingDisableDirective } from '../../shared/directives/global-loading-disable.directive';
import { FileIconService, FileIconInfo } from '../../core/services/file-icon.service';
import { ReservationService } from '../../core/services/reservation.service';
import { take } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
@Component({
  selector: 'app-contract',
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    PaginatorModule,
    TagModule,
    AvatarModule,
    TooltipModule,
    MenuModule,
    ConfirmDialogModule,
    ToastModule,
    IconFieldModule,
    InputIconModule,
    DrawerModule,
    DialogModule,
    TextareaModule,
    HasPermissionDirective,
    LoadingSpinnerComponent],
  templateUrl: './contract.html',
  styleUrl: './contract.scss',
  providers: [MessageService, ConfirmationService]
})
export class ContractComponent implements OnInit {
  @ViewChild('menu') menu!: Menu;
  
  contracts: Contract[] = [];
  loading: boolean = false;
  
  // Make Math available in template
  Math = Math;
  
  // Pagination
  currentPage: number = 0;
  pageSize: number = 10;
  pageSizes: number[] = [10, 25, 50, 100];
  totalContracts: number = 0;
  
  // Sorting
  sortField: string = 'createdAt';
  sortOrder: 1 | -1 = -1;
  
  // Filters
  searchText: string = '';
  selectedStatus: string = '';
  
  // Status options
  statusOptions: any[] = [
    { label: 'All Status', value: '' },
    { label: 'Draft', value: 'DRAFT' },
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Expired', value: 'EXPIRED' },
    { label: 'Terminated', value: 'TERMINATED' },
    { label: 'Rejected', value: 'REJECTED' }
  ];
  
  // Action menu
  actionMenuItems: any[] = [];
  selectedContract: Contract | null = null;
  
  // Drawer for contract review
  isDrawerVisible = false;
  selectedContractForReview: Contract | null = null;
  contractReviewLoading = false;
  
  // Additional loading states
  updatingContractStatus = false;
  
  // Track which terminated contracts can create new contracts
  canCreateNewContract: { [contractId: string]: boolean } = {};
  checkingContractCreation: { [contractId: string]: boolean } = {};

  // Rejection reason dialog
  showRejectionDialog = false;
  rejectionReason = '';
  contractToReject: Contract | null = null;

  // Termination reason dialog
  showTerminationDialog = false;
  terminationReason = '';
  contractToTerminate: string | null = null;

  // User context and filtering
  currentUser: any = null;
  selectedSubscriberId: string | null = null;
  subscriberTermsConditionsUrl: string = '';
  subscriberPrivacyPolicyUrl: string = '';
  subscriberTermsConditionsFileName: string = '';
  subscriberPrivacyPolicyFileName: string = '';

  constructor(
    @Inject(ContractService) private contractService: ContractService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private sanitizer: DomSanitizer,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private permissionService: PermissionService,
    private subscriptionContextService: SubscriptionContextService,
    private subscriberService: SubscriberService,
    private globalLoadingService: GlobalLoadingService,
    private fileIconService: FileIconService,
    private reservationService: ReservationService) {}

  ngOnInit(): void {
    this.initializeUserContext();
    // Check for contractId query parameter to auto-open contract
    // Check snapshot first (for immediate navigation), then subscribe for changes
    const snapshotParams = this.route.snapshot.queryParams;
    console.log('Contract Component - Query params (snapshot):', snapshotParams);
    if (snapshotParams['contractId']) {
      console.log('Contract Component - Found contractId in snapshot:', snapshotParams['contractId']);
      // Use setTimeout to ensure component is fully initialized
      setTimeout(() => {
        this.checkAndOpenContractFromQuery(snapshotParams['contractId']);
      }, 200);
    }
    
    // Also subscribe to param changes (for future navigations)
    this.route.queryParams.pipe(take(1)).subscribe(params => {
      console.log('Contract Component - Query params (observable):', params);
      if (params['contractId'] && params['contractId'] !== snapshotParams['contractId']) {
        console.log('Contract Component - Found contractId in observable:', params['contractId']);
        this.checkAndOpenContractFromQuery(params['contractId']);
      }
    });
  }

  initializeUserContext(): void {
    // Get current user context
    this.currentUser = this.authService.getLoggedInUser();
    
    // Get selected subscriber ID from subscription context
    this.selectedSubscriberId = this.subscriptionContextService.getSelectedSubscriberId();
    
    console.log('Contract Component - Current User:', this.currentUser);
    console.log('Contract Component - Selected Subscriber ID:', this.selectedSubscriberId);
    console.log('Contract Component - User Role Details:', {
      roleId: this.currentUser?.roleId,
      roleIdentification: this.currentUser?.roleId?.identification,
      isFSR: this.currentUser?.roleId?.identification === 'FSR'
    });
    
    // Load contracts after user context is initialized
    this.loadContracts();
    this.loadSubscriberDocuments();
  }

  // Check if current user is FSR
  isFSR(): boolean {
    const roleIdentification = this.currentUser?.roleId?.identification || this.currentUser?.role?.identification;
    return roleIdentification === 'FSR';
  }

  loadContracts(): void {
    this.globalLoadingService.executeWithLoading(async () => {
      this.loading = true;
      
      const request: PagedContractsRequest = {
        pageIndex: this.currentPage + 1,
        pageSize: this.pageSize,
        sortField: this.sortField,
        sortOrder: this.sortOrder,
        filters: {
          searchText: this.searchText,
          status: this.selectedStatus
        }
      };

      // Apply FSR-specific filtering if user is FSR
      const roleIdentification = this.currentUser?.roleId?.identification || this.currentUser?.role?.identification;
      
      console.log('Contract Component - FSR Check:', {
        hasCurrentUser: !!this.currentUser,
        hasRoleId: !!this.currentUser?.roleId,
        hasRole: !!this.currentUser?.role,
        roleIdIdentification: this.currentUser?.roleId?.identification,
        roleIdentification: this.currentUser?.role?.identification,
        finalRoleIdentification: roleIdentification,
        isFSR: roleIdentification === 'FSR',
        userId: this.currentUser?._id
      });

      if (roleIdentification === 'FSR') {
        // Filter contracts to only show those related to this FSR
        request.filters.fsrId = this.currentUser._id;
        console.log('Contract Component - Applying FSR filter for user:', this.currentUser._id);
      } else {
        console.log('Contract Component - Not applying FSR filter. Role:', roleIdentification);
      }

      // Subscription filtering is handled automatically by the interceptor
      // which adds selectedSubscriberId to the request

      try {
        const response: any = await this.contractService.getPagedContracts(request).toPromise();
        console.log('Contracts API Response:', response);
        this.contracts = response.data || [];
        this.totalContracts = response.metadata?.[0]?.total || 0;
        console.log('Contracts loaded:', this.contracts.length);
      } catch (error: any) {
        console.error('Error loading contracts:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load contracts'
        });
      } finally {
        this.loading = false;
      }
    });
  }

  checkAndOpenContractFromQuery(contractId: string): void {
    if (!contractId) {
      console.log('Contract Component - checkAndOpenContractFromQuery called with no contractId');
      return;
    }
    
    console.log('Contract Component - Fetching contract by ID:', contractId);
    // Fetch the contract directly by ID
    this.contractReviewLoading = true;
    this.contractService.getContractById(contractId).subscribe({
      next: (response: any) => {
        console.log('Contract Component - Contract fetch response:', response);
        if (response.data) {
          const contract = response.data;
          console.log('Contract Component - Opening drawer for contract:', contract._id);
          // Use setTimeout to ensure component is fully rendered before opening drawer
          setTimeout(() => {
            // Open the contract detail view
            this.reviewContract(contract);
            console.log('Contract Component - Drawer should be visible:', this.isDrawerVisible);
            // Remove the query parameter from URL to clean it up
            this.router.navigate([], {
              relativeTo: this.route,
              queryParams: { contractId: null },
              queryParamsHandling: 'merge',
              replaceUrl: true
            });
          }, 100);
        } else {
          console.log('Contract Component - No contract data in response');
        }
        this.contractReviewLoading = false;
      },
      error: (error) => {
        console.error('Contract Component - Error loading contract:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load contract details'
        });
        this.contractReviewLoading = false;
        // Remove the query parameter even on error
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { contractId: null },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      }
    });
  }

  applyFilters(): void {
    this.currentPage = 0;
    this.loadContracts();
  }

  resetFilters(): void {
    this.searchText = '';
    this.selectedStatus = '';
    this.currentPage = 0;
    this.loadContracts();
  }

  toggleSort(): void {
    this.sortOrder = this.sortOrder === -1 ? 1 : -1;
    this.loadContracts();
  }

  onPageChange(event: any): void {
    this.currentPage = event.page;
    this.pageSize = event.rows;
    this.loadContracts();
  }

  getStatusBadgeSeverity(status: string, endDate?: string | Date): string {
    if (status?.toUpperCase() === 'ACTIVE' && this.isExpiringSoon(endDate)) {
      return 'warn';
    }
    
    switch (status?.toUpperCase()) {
      case 'DRAFT':
        return 'info';
      case 'ACTIVE':
        return 'success';
      case 'EXPIRED':
        return 'danger';
      case 'TERMINATED':
        return 'danger';
      case 'REJECTED':
        return 'secondary';
      default:
        return 'info';
    }
  }

  getStatusLabel(status: string, endDate?: string | Date): string {
    if (status?.toUpperCase() === 'ACTIVE' && this.isExpiringSoon(endDate)) {
      return 'Expiring Soon';
    }
    
    switch (status?.toUpperCase()) {
      case 'DRAFT':
        return 'Draft';
      case 'ACTIVE':
        return 'Active';
      case 'EXPIRED':
        return 'Expired';
      case 'TERMINATED':
        return 'Terminated';
      case 'REJECTED':
        return 'Rejected';
      default:
        return status || 'Unknown';
    }
  }

  formatDate(date: string | Date): string {
    if (!date) return 'N/A';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  formatDateTime(date: string | Date): string {
    if (!date) return 'N/A';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  }

  isExpiringSoon(endDate?: string | Date): boolean {
    if (!endDate) return false;
    
    const endDateObj = typeof endDate === 'string' ? new Date(endDate) : endDate;
    const today = new Date();
    const oneWeekFromNow = new Date(today.getTime() + (7 * 24 * 60 * 60 * 1000));
    
    return endDateObj <= oneWeekFromNow && endDateObj >= today;
  }

  showActionMenu(event: Event, contract: Contract): void {
    this.selectedContract = contract;
    
    // Build action menu based on contract status
    this.actionMenuItems = [];
    
    if (contract.status?.toUpperCase() === 'DRAFT') {
      // Only show Review Contract if user has permission
      if (this.permissionService.hasPermission('contract.review')) {
        this.actionMenuItems.push({
          label: 'Review Contract',
          icon: 'pi pi-eye',
          command: () => this.reviewContract(contract)
        });
      }
    } else {
      this.actionMenuItems.push({
        label: 'View Details',
        icon: 'pi pi-eye',
        command: () => this.viewContract(contract)
      });

      // Only add Update Contract option if not terminated
      if (contract.status?.toUpperCase() !== 'TERMINATED') {
        this.actionMenuItems.push({
          label: 'Update Contract',
          icon: 'pi pi-pencil',
          command: () => this.updateContract(contract)
        });
      }

      // Add terminate option for ACTIVE contracts or Expiring Soon contracts
      if ((contract.status?.toUpperCase() === 'ACTIVE' || 
          (contract.status?.toUpperCase() === 'ACTIVE' && this.isExpiringSoon(contract.endDate))) &&
          this.permissionService.hasPermission('contract.terminate')) {
        this.actionMenuItems.push({
          label: 'Terminate Contract',
          icon: 'pi pi-times-circle',
          command: () => this.terminateContract(contract._id)
        });
      }
    }
    
    this.menu.toggle(event);
  }

  reviewContract(contract: Contract): void {
    this.selectedContractForReview = contract;
    this.isDrawerVisible = true;
    
    // Check if we can create a new contract for terminated contracts
    if (contract.status?.toUpperCase() === 'TERMINATED') {
      this.checkIfCanCreateNewContract(contract);
    }
  }

  viewContract(contract: Contract): void {
    this.selectedContractForReview = contract;
    this.isDrawerVisible = true;
    
    // Check if we can create a new contract for terminated contracts
    if (contract.status?.toUpperCase() === 'TERMINATED') {
      this.checkIfCanCreateNewContract(contract);
    }
  }

  updateContract(contract: Contract): void {
    if (contract.status?.toUpperCase() === 'TERMINATED') {
      this.messageService.add({
        severity: 'warn',
        summary: 'Cannot Update',
        detail: 'Terminated contracts cannot be updated. Please create a new contract instead.'
      });
      return;
    }
    this.router.navigate(['/admin/add-edit-contract/update', contract._id]);
  }

  addNewContract(): void {
    this.messageService.add({
      severity: 'info',
      summary: 'Add Contract',
      detail: 'Navigate to add contract page'
    });
  }

  closeContractReview(): void {
    this.isDrawerVisible = false;
    this.selectedContractForReview = null;
  }

  getCustomerName(contract: Contract): string {
    return (contract.customerId as any)?.businessName || 'Customer Name';
  }

  getProductName(product: any): string {
    return product?.name || product || 'Unknown Product';
  }

  hasProductValues(contract: Contract): boolean {
    if (!contract || !contract.productValues) return false;
    if (contract.productValues instanceof Map) {
      return contract.productValues.size > 0;
    }
    if (typeof contract.productValues === 'object') {
      return Object.keys(contract.productValues).length > 0;
    }
    return false;
  }

  getProductValue(product: any, productValues: any): string {
    if (!productValues) return '';
    
    // Handle both Map and object formats
    let productId: string;
    if (typeof product === 'string') {
      productId = product;
    } else if (product?._id) {
      // Handle both ObjectId and string formats
      productId = typeof product._id === 'object' ? product._id.toString() : product._id;
    } else {
      return '';
    }
    
    let value: number | undefined;
    if (productValues instanceof Map) {
      value = productValues.get(productId);
    } else if (typeof productValues === 'object') {
      // Try exact match first
      value = productValues[productId];
      
      // If not found, try matching with string conversion
      if (value === undefined) {
        const keys = Object.keys(productValues);
        const matchingKey = keys.find(key => key.toString() === productId.toString());
        if (matchingKey) {
          value = productValues[matchingKey];
        }
      }
    }
    
    if (value === undefined || value === null) return '';
    return this.formatCurrency(value);
  }

  getFSRName(contract: Contract): string {
    return `${(contract.fsrId as any)?.firstName || ''} ${(contract.fsrId as any)?.lastName || ''}`.trim() || 'FSR Name';
  }

  getReservationRefNo(contract: Contract): string {
    // Handle both populated object and string ID cases
    if (typeof contract.reservationId === 'object' && contract.reservationId !== null) {
      return (contract.reservationId as any)?.refNo || 'N/A';
    }
    return 'N/A';
  }

  getProductNames(products: any[]): string {
    if (!products || products.length === 0) return 'No products';
    return products.map(p => p.name).join(', ');
  }

  getApproverName(contract: Contract): string {
    if (!contract.approverId) return 'Not Approved';
    const approver = contract.approverId as any;
    return `${approver.firstName || ''} ${approver.lastName || ''}`.trim() || 'Not Approved';
  }

  approveContract(contractId: string): void {
    this.confirmationService.confirm({
      message: 'Are you sure you want to approve this contract?',
      header: 'Approve Contract',
      icon: 'pi pi-exclamation-triangle',
      rejectButtonStyleClass: 'p-button-outlined p-button-secondary',
      accept: () => {
        this.updateContractStatus(contractId, 'ACTIVE');
      }
    });
  }

  denyContract(contractId: string): void {
    const contract = this.contracts.find(c => c._id === contractId);
    if (!contract) return;
    
    this.contractToReject = contract;
    this.rejectionReason = '';
    this.showRejectionDialog = true;
  }

  confirmRejection(): void {
    if (!this.contractToReject || !this.rejectionReason || this.rejectionReason.trim() === '') {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please provide a rejection reason'
      });
      return;
    }

    this.updateContractStatus(this.contractToReject._id, 'REJECTED', this.rejectionReason);
    this.showRejectionDialog = false;
    this.contractToReject = null;
    this.rejectionReason = '';
  }

  cancelRejection(): void {
    this.showRejectionDialog = false;
    this.contractToReject = null;
    this.rejectionReason = '';
  }

  resubmitContract(contractId: string): void {
    this.confirmationService.confirm({
      message: 'Are you sure you want to resubmit this contract for review?',
      header: 'Resubmit Contract',
      icon: 'pi pi-exclamation-triangle',
      rejectButtonStyleClass: 'p-button-outlined p-button-secondary',
      accept: () => {
        // Find the contract to get its productValues
        const contract = this.contracts.find(c => c._id === contractId) || this.selectedContractForReview;
        this.updateContractStatus(contractId, 'DRAFT', undefined, contract);
      }
    });
  }

  terminateContract(contractId: string): void {
    this.contractToTerminate = contractId;
    this.terminationReason = '';
    this.showTerminationDialog = true;
  }

  confirmTermination(): void {
    if (!this.contractToTerminate) return;
    
    this.updateContractStatus(this.contractToTerminate, 'TERMINATED', undefined, null, this.terminationReason);
    this.showTerminationDialog = false;
    this.contractToTerminate = null;
    this.terminationReason = '';
  }

  cancelTermination(): void {
    this.showTerminationDialog = false;
    this.contractToTerminate = null;
    this.terminationReason = '';
  }

  async checkIfCanCreateNewContract(contract: Contract): Promise<void> {
    const contractId = contract._id;
    
    // If already checking or already determined, skip
    if (this.checkingContractCreation[contractId] || this.canCreateNewContract.hasOwnProperty(contractId)) {
      return;
    }
    
    this.checkingContractCreation[contractId] = true;
    
    try {
      const reservationId = typeof contract.reservationId === 'string' 
        ? contract.reservationId 
        : (contract.reservationId as any)?._id || contract.reservationId;
      
      if (!reservationId) {
        this.canCreateNewContract[contractId] = false;
        return;
      }
      
      // Load reservation to get products
      const reservationResponse: any = await firstValueFrom(this.reservationService.getReservationById(reservationId));
      const reservation = reservationResponse?.data || reservationResponse;
      
      if (!reservation || !reservation.products || reservation.products.length === 0) {
        this.canCreateNewContract[contractId] = false;
        return;
      }
      
      const reservationProductIds = reservation.products.map((p: any) => 
        typeof p === 'string' ? p : (p._id || p)
      ).map((id: any) => id.toString());
      
      // Load all contracts for the customer
      const customerId = typeof contract.customerId === 'string' 
        ? contract.customerId 
        : (contract.customerId as any)?._id || contract.customerId;
      
      const contractsResponse: any = await firstValueFrom(this.contractService.getContractsByCustomerId(customerId));
      const contracts = contractsResponse?.data || contractsResponse || [];
      
      // Filter out terminated contracts, expired contracts, and the current contract
      const activeContracts = contracts.filter((c: Contract) => 
        c._id !== contractId && 
        c.status?.toUpperCase() !== 'TERMINATED' &&
        c.status?.toUpperCase() !== 'EXPIRED'
      );
      
      // Get all product IDs that already have contracts
      const contractedProductIds = new Set<string>();
      activeContracts.forEach((c: Contract) => {
        if (c.products) {
          c.products.forEach((productId: string) => {
            const pid = typeof productId === 'string' ? productId : (productId as any)?._id || productId;
            contractedProductIds.add(pid.toString());
          });
        }
      });
      
      // Check if there are any products from reservation that don't have contracts
      const availableProducts = reservationProductIds.filter((productId: string) => {
        return !contractedProductIds.has(productId);
      });
      
      this.canCreateNewContract[contractId] = availableProducts.length > 0;
    } catch (error) {
      console.error('Error checking if can create new contract:', error);
      this.canCreateNewContract[contractId] = false;
    } finally {
      this.checkingContractCreation[contractId] = false;
    }
  }

  addNewContractForCustomer(contract: Contract): void {
    const customerId = typeof contract.customerId === 'string' 
      ? contract.customerId 
      : (contract.customerId as any)?._id || contract.customerId;
    
    const reservationId = typeof contract.reservationId === 'string' 
      ? contract.reservationId 
      : (contract.reservationId as any)?._id || contract.reservationId;
    
    if (customerId) {
      const queryParams: any = { customerId: customerId };
      if (reservationId) {
        queryParams.reservationId = reservationId;
      }
      
      this.router.navigate(['/admin/add-edit-contract'], { 
        queryParams: queryParams
      });
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Unable to determine customer ID'
      });
    }
  }

  private updateContractStatus(contractId: string, status: string, rejectionReason?: string, contract?: Contract | null, terminationReason?: string): void {
    this.updatingContractStatus = true;
    
    const updateData: any = { status };
    if (rejectionReason) {
      updateData.rejectionReason = rejectionReason;
    }
    if (terminationReason) {
      updateData.terminationReason = terminationReason;
    }
    
    // Preserve productValues when resubmitting (status change to DRAFT)
    if (status === 'DRAFT' && contract && contract.productValues) {
      // Convert productValues Map to object if needed
      if (contract.productValues instanceof Map) {
        const productValuesObj: { [key: string]: number } = {};
        contract.productValues.forEach((value: number, key: string) => {
          productValuesObj[key] = value;
        });
        updateData.productValues = productValuesObj;
      } else if (typeof contract.productValues === 'object') {
        updateData.productValues = contract.productValues;
      }
    }
    
    this.contractService.updateContract(contractId, updateData).subscribe({
      next: (response) => {
        const statusMessage = status === 'DRAFT' ? 'resubmitted' : status.toLowerCase();
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `Contract ${statusMessage} successfully`
        });
        
        // Close drawer and refresh contracts
        this.closeContractReview();
        this.loadContracts();
        this.updatingContractStatus = false;
      },
      error: (error) => {
        console.error('Error updating contract status:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.error?.message || 'Failed to update contract status'
        });
        this.updatingContractStatus = false;
      }
    });
  }

  // Method to safely render HTML content with proper link and list handling
  sanitizeHtml(content: string): SafeHtml {
    if (!content) return '';
    
    // Process the content to ensure links and lists work correctly
    let processedContent = content;
    
    // Ensure links have proper target="_blank" and rel="noopener noreferrer"
    processedContent = processedContent.replace(
      /<a\s+href=/gi,
      '<a target="_blank" rel="noopener noreferrer" href='
    );
    
    // Add CSS classes to links for styling
    processedContent = processedContent.replace(
      /<a\s+/gi,
      '<a class="content-link" '
    );
    
    // Add CSS classes to lists for proper styling
    processedContent = processedContent.replace(
      /<ul\s+/gi,
      '<ul class="content-list content-ul" '
    );
    
    processedContent = processedContent.replace(
      /<ol\s+/gi,
      '<ol class="content-list content-ol" '
    );
    
    // Handle lists without attributes
    processedContent = processedContent.replace(
      /<ul>/gi,
      '<ul class="content-list content-ul">'
    );
    
    processedContent = processedContent.replace(
      /<ol>/gi,
      '<ol class="content-list content-ol">'
    );
    
    // Add CSS classes to list items
    processedContent = processedContent.replace(
      /<li\s+/gi,
      '<li class="content-list-item" '
    );
    
    // Handle list items without attributes
    processedContent = processedContent.replace(
      /<li>/gi,
      '<li class="content-list-item">'
    );
    
    return this.sanitizer.bypassSecurityTrustHtml(processedContent);
  }

  // Method to open document in new tab
  openDocument(documentUrl: string): void {
    if (!documentUrl) return;
    
    // Simple: if it's not a full URL, add the S3 base URL
    const fullUrl = documentUrl.startsWith('http') 
      ? documentUrl 
      : `https://purpledev.s3.us-east-2.amazonaws.com/office-global/${documentUrl}`;
    
    window.open(fullUrl, '_blank');
  }

  // Helper method to get attachment display name
  getDocumentDisplayName(documentUrl: string, index: number): string {
    if (!documentUrl) return '';
    const resolvedName = this.resolveSubscriberDocumentName(documentUrl);
    if (resolvedName) {
      return resolvedName;
    }

    const fileName = this.extractFileName(documentUrl);
    if (fileName) {
      return fileName;
    }

    return `Attachment ${index + 1}`;
  }

  // Get file icon information
  getFileIcon(fileUrl: string): FileIconInfo {
    return this.fileIconService.getFileIcon(fileUrl);
  }

  private loadSubscriberDocuments(): void {
    const subscriberId =
      this.selectedSubscriberId ||
      this.currentUser?.subscriberId?._id ||
      this.currentUser?.subscriberId;

    if (!subscriberId) {
      this.subscriberTermsConditionsUrl = '';
      this.subscriberPrivacyPolicyUrl = '';
      this.subscriberTermsConditionsFileName = '';
      this.subscriberPrivacyPolicyFileName = '';
      return;
    }

    this.subscriberService
      .findSubscriber(subscriberId)
      .then((subscriber: any) => {
        let actualSubscriber = subscriber;
        if (subscriber && subscriber.subscriber) {
          actualSubscriber = subscriber.subscriber;
        }

        const termsUrl = this.normalizeDocumentIdentifier(actualSubscriber?.termsConditions || '');
        const privacyUrl = this.normalizeDocumentIdentifier(actualSubscriber?.privacyPolicy || '');

        this.subscriberTermsConditionsUrl = termsUrl;
        this.subscriberPrivacyPolicyUrl = privacyUrl;
        this.subscriberTermsConditionsFileName = this.extractFileName(termsUrl);
        this.subscriberPrivacyPolicyFileName = this.extractFileName(privacyUrl);
      })
      .catch((error: any) => {
        console.error('Failed to load subscriber documents for contracts:', error);
        this.subscriberTermsConditionsUrl = '';
        this.subscriberPrivacyPolicyUrl = '';
        this.subscriberTermsConditionsFileName = '';
        this.subscriberPrivacyPolicyFileName = '';
      });
  }

  private resolveSubscriberDocumentName(documentUrl: string): string | null {
    const normalizedUrl = this.normalizeDocumentIdentifier(documentUrl);
    const fileName = this.extractFileName(normalizedUrl);

    if (this.matchesSubscriberDocument(normalizedUrl, fileName, this.subscriberTermsConditionsUrl, this.subscriberTermsConditionsFileName)) {
      return 'Terms and Conditions';
    }

    if (this.matchesSubscriberDocument(normalizedUrl, fileName, this.subscriberPrivacyPolicyUrl, this.subscriberPrivacyPolicyFileName)) {
      return 'Privacy Policy';
    }

    return null;
  }

  private matchesSubscriberDocument(
    documentUrl: string,
    documentFileName: string,
    subscriberUrl: string,
    subscriberFileName: string
  ): boolean {
    if (!subscriberUrl && !subscriberFileName) {
      return false;
    }

    const normalizedSubscriberUrl = this.normalizeDocumentIdentifier(subscriberUrl);
    const normalizedSubscriberFileName = this.extractFileName(normalizedSubscriberUrl);
    const compareFileName = subscriberFileName || normalizedSubscriberFileName;

    return (
      (!!normalizedSubscriberUrl && normalizedSubscriberUrl === documentUrl) ||
      (!!compareFileName && compareFileName === documentFileName)
    );
  }

  private normalizeDocumentIdentifier(url: string): string {
    if (!url) {
      return '';
    }
    const trimmedUrl = url.trim();
    if (!trimmedUrl) {
      return '';
    }
    const [pathWithoutQuery] = trimmedUrl.split('?');
    const [pathWithoutHash] = pathWithoutQuery.split('#');
    return pathWithoutHash;
  }

  private extractFileName(url: string): string {
    if (!url) {
      return '';
    }

    const cleanedUrl = this.normalizeDocumentIdentifier(url);
    const segments = cleanedUrl.split('/');
    const lastSegment = segments.pop() || '';

    return lastSegment.trim();
  }

}
